/**
 * Visitor profiles from the realtime feed: every event carrying the same
 * `sessionId` is one visit. Only what the feed actually reports is shown; an
 * event without a session id stands alone rather than being guessed into one.
 */

export type RealtimeEventKind = 'view' | 'click' | 'form'

export interface RealtimeActivity {
  id: string
  type: 'session' | 'metric'
  time: string
  page: string
  sessionId?: string
  country?: string
  region?: string
  device?: string
  browser?: string
  deviceGroup?: string
  utmSource?: string
  utmMedium?: string
  referrer?: string
  action?: string
  target?: string
  secondary?: string
}

export interface VisitorProfile {
  key: string
  sessionId?: string
  country?: string
  region?: string
  device?: string
  browser?: string
  deviceGroup?: string
  /** utm_source, else referrer group, else null (direct or unknown) */
  source: string | null
  medium?: string
  landingPage: string
  currentPage: string
  firstSeen: string
  lastSeen: string
  /** Oldest first */
  events: RealtimeActivity[]
  views: number
  clicks: number
  forms: number
  /** Seconds between the first and last event of the visit */
  durationSeconds: number
}

/** A visitor counts as on site while their last event is under this old. */
export const ACTIVE_WINDOW_MS = 5 * 60 * 1000

export function activityKind(event: Pick<RealtimeActivity, 'type' | 'action'>): RealtimeEventKind {
  if (event.type === 'session') return 'view'
  return event.action === 'click' ? 'click' : 'form'
}

const timeOf = (e: RealtimeActivity) => new Date(e.time).getTime()
const firstDefined = <T,>(events: RealtimeActivity[], pick: (e: RealtimeActivity) => T | undefined | null) => {
  for (const e of events) {
    const v = pick(e)
    if (v != null && v !== '') return v
  }
  return undefined
}

export function profileKeyOf(event: RealtimeActivity): string {
  return event.sessionId ? `s:${event.sessionId}` : `e:${event.id}`
}

/** Profiles newest-activity first. Events with unparseable times are dropped. */
export function buildVisitorProfiles(events: RealtimeActivity[]): VisitorProfile[] {
  const groups = new Map<string, RealtimeActivity[]>()
  for (const e of events) {
    if (!Number.isFinite(timeOf(e))) continue
    const key = profileKeyOf(e)
    const list = groups.get(key)
    if (list) list.push(e)
    else groups.set(key, [e])
  }

  const profiles: VisitorProfile[] = []
  for (const [key, list] of groups) {
    const ordered = [...list].sort((a, b) => timeOf(a) - timeOf(b))
    const newestFirst = [...ordered].reverse()
    const views = ordered.filter((e) => activityKind(e) === 'view')
    const first = ordered[0]
    const last = ordered[ordered.length - 1]
    profiles.push({
      key,
      sessionId: first.sessionId,
      country: firstDefined(ordered, (e) => e.country),
      region: firstDefined(ordered, (e) => e.region),
      device: firstDefined(ordered, (e) => (e.device && e.device !== 'Unknown' ? e.device : undefined)),
      browser: firstDefined(ordered, (e) => e.browser),
      deviceGroup: firstDefined(ordered, (e) => e.deviceGroup),
      source: firstDefined(ordered, (e) => e.utmSource?.trim() || e.referrer?.trim()) ?? null,
      medium: firstDefined(ordered, (e) => e.utmMedium),
      landingPage: (views[0] ?? first).page || '/',
      currentPage: (firstDefined(newestFirst, (e) => (activityKind(e) === 'view' ? e.page : undefined)) ?? last.page) || '/',
      firstSeen: first.time,
      lastSeen: last.time,
      events: ordered,
      views: views.length,
      clicks: ordered.filter((e) => activityKind(e) === 'click').length,
      forms: ordered.filter((e) => activityKind(e) === 'form').length,
      durationSeconds: Math.max(0, Math.round((timeOf(last) - timeOf(first)) / 1000)),
    })
  }
  return profiles.sort((a, b) => new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime())
}

export function isOnSite(profile: Pick<VisitorProfile, 'lastSeen'>, now = Date.now()): boolean {
  return now - new Date(profile.lastSeen).getTime() < ACTIVE_WINDOW_MS
}

/** "45s", "3m 20s", "1h 4m" */
export function formatVisitDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  if (m < 60) return seconds % 60 ? `${m}m ${seconds % 60}s` : `${m}m`
  return `${Math.floor(m / 60)}h ${m % 60}m`
}

/** A feed event as the live card parses it: the activity plus raw extras. */
export interface RealtimeFeedEvent extends RealtimeActivity {
  sessionLength?: number
  x?: number
  y?: number
}

const deviceOf = (item: any) =>
  item.client_os && item.client_browser ? `${item.client_os} - ${item.client_browser}` : item.device_type || 'Unknown'

const regionOf = (item: any) => item.region || item.subdivision || item.state || item.province

/**
 * Normalise the realtime endpoint (`live_feed`, plus the legacy `sessions` /
 * `metrics` arrays) into one newest-first list. Shared by the Overview live
 * card and the visitor profile page so both read the same visit.
 */
export function parseRealtimeFeed(data: { liveFeed?: any[]; sessions?: any[]; metrics?: any[] } | null | undefined): RealtimeFeedEvent[] {
  const visitFields = (item: any) => ({
    sessionId: item.session_id || undefined,
    deviceGroup: item.device_group || undefined,
    utmMedium: item.utm_medium || undefined,
    referrer: item.referer_group || item.referrer || undefined,
  })
  const events: RealtimeFeedEvent[] = []
  const seenIds = new Set<string>()
  const push = (event: RealtimeFeedEvent) => {
    if (seenIds.has(event.id)) return
    seenIds.add(event.id)
    events.push(event)
  }

  if (Array.isArray(data?.liveFeed)) {
    data.liveFeed.forEach((item: any, index: number) => {
      if (!item) return
      const time = item.time || item.timestamp || item.created_at || item.date
      if (!time) return
      const page = item.page || item.page_path || item.path || item.url || '/'
      const common = {
        ...visitFields(item),
        time,
        page,
        country: item.country || item.country_code,
        region: regionOf(item),
        device: deviceOf(item),
        browser: item.client_browser || item.browser,
        utmSource: item.utm_source,
      }
      const isSession = item.event_type === 'session' || (!item.event_id && !item.action)
      if (isSession) {
        push({ ...common, id: `session-${item.view_id || item.id || index}-${time}`, type: 'session', sessionLength: item.session_length })
      } else {
        push({
          ...common,
          id: `metric-${item.event_id || item.id || index}-${time}`,
          type: 'metric',
          action: item.action || 'click',
          target: item.target || item.element,
          secondary: item.secondary,
          x: item.x,
          y: item.y,
        })
      }
    })
  }

  // Legacy format support
  if (Array.isArray(data?.sessions)) {
    data.sessions.forEach((session: any, index: number) => {
      if (!session?.time) return
      push({
        ...visitFields(session),
        id: `session-${session.view_id || index}-${session.time}`,
        type: 'session',
        time: session.time,
        page: session.page || '/',
        country: session.country,
        region: regionOf(session),
        device: session.client_os && session.client_browser ? `${session.client_os} - ${session.client_browser}` : 'Unknown',
        browser: session.client_browser,
        utmSource: session.utm_source,
        sessionLength: session.session_length,
      })
    })
  }

  if (Array.isArray(data?.metrics)) {
    data.metrics.forEach((metric: any, index: number) => {
      if (!metric?.time) return
      push({
        ...visitFields(metric),
        id: `metric-${metric.event_id || index}-${metric.time}`,
        type: 'metric',
        time: metric.time,
        page: metric.page || '/',
        action: metric.action || 'click',
        target: metric.target,
        secondary: metric.secondary,
        x: metric.x,
        y: metric.y,
        country: metric.country,
        region: regionOf(metric),
        device: metric.client_os && metric.client_browser ? `${metric.client_os} - ${metric.client_browser}` : 'Unknown',
        browser: metric.client_browser,
        utmSource: metric.utm_source || undefined,
      })
    })
  }

  return events.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
}

const NAME_ADJECTIVES = [
  'Amber', 'Quiet', 'Silver', 'Brisk', 'Calm', 'Bold', 'Swift', 'Gentle', 'Bright', 'Misty',
  'Golden', 'Rapid', 'Clever', 'Lucky', 'Sunny', 'Velvet', 'Coral', 'Hazel', 'Jade', 'Rusty',
  'Frosty', 'Mellow', 'Nimble', 'Proud', 'Steady', 'Wild', 'Witty', 'Cosmic', 'Dapper', 'Sly',
  'Polar', 'Cedar',
]
const NAME_ANIMALS = [
  'Otter', 'Heron', 'Fox', 'Lynx', 'Finch', 'Marten', 'Badger', 'Falcon', 'Hare', 'Owl',
  'Panda', 'Raven', 'Seal', 'Tiger', 'Wren', 'Bison', 'Crane', 'Dolphin', 'Gecko', 'Ibis',
  'Koala', 'Lemur', 'Moose', 'Newt', 'Puffin', 'Quail', 'Robin', 'Stoat', 'Toucan', 'Walrus',
  'Yak', 'Zebra',
]

function hashKey(key: string): number {
  let h = 2166136261
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/**
 * A stable, made-up name for an anonymous visit ("Amber Otter"). Derived from
 * the profile key only, so the feed row and the profile page always agree and
 * nothing about the person is stored or implied.
 */
export function visitorName(key: string): string {
  const h = hashKey(key)
  return `${NAME_ADJECTIVES[h % NAME_ADJECTIVES.length]} ${NAME_ANIMALS[Math.floor(h / NAME_ADJECTIVES.length) % NAME_ANIMALS.length]}`
}

/** "Amber Otter" → "AO" */
export function visitorInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('')
}

/**
 * Seconds spent on the page view at `index` of an oldest-first visit: the gap
 * to the next page view. Null for the latest view (still open, or the exit).
 */
export function timeOnPage(events: RealtimeActivity[], index: number): number | null {
  const next = events.slice(index + 1).find((e) => activityKind(e) === 'view')
  if (!next) return null
  return Math.max(0, Math.round((timeOf(next) - timeOf(events[index])) / 1000))
}
